/**
 * Server-side Schema.org validation using the latest vocabulary.
 * Reads pre-downloaded schema.org definitions from disk.
 */

import vocabFile from './schema-org-vocab.json';

export interface ValidationError {
  path: string;
  message: string;
  type: 'unknown_property' | 'invalid_type' | 'invalid_property_for_type';
}

// Cache the vocabulary in memory after first read
let vocabCache: {
  properties: Set<string>;
  types: Set<string>;
  typeHierarchy: Record<string, string[]>;
  propertyDomains: Record<string, string[]>;
} | null = null;

function loadVocabulary() {
  vocabCache = {
    properties: new Set(vocabFile.properties),
    types: new Set(vocabFile.types),
    typeHierarchy: vocabFile.typeHierarchy,
    propertyDomains: vocabFile.propertyDomains,
  };

  return vocabCache;
}

function getTypeAncestors(type: string, hierarchy: Record<string, string[]>): Set<string> {
  const ancestors = new Set<string>([type]);
  const queue = [type];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const parents = hierarchy[current] ?? [];
    for (const parent of parents) {
      if (!ancestors.has(parent)) {
        ancestors.add(parent);
        queue.push(parent);
      }
    }
  }

  return ancestors;
}

function validateObject(obj: unknown, vocab: NonNullable<typeof vocabCache>, path = ''): ValidationError[] {
  if (obj == null || typeof obj !== 'object') return [];
  const errors: ValidationError[] = [];

  if (Array.isArray(obj)) {
    obj.forEach((item, i) => {
      errors.push(...validateObject(item, vocab, path ? `${path}[${i}]` : `[${i}]`));
    });
    return errors;
  }

  const record = obj as Record<string, unknown>;
  const objType = record['@type'] as string | undefined;

  // Validate @type
  if (objType && !vocab.types.has(objType)) {
    errors.push({
      path: path ? `${path}.@type` : '@type',
      message: `Unknown type "${objType}"`,
      type: 'invalid_type',
    });
  }

  const typeAncestors = objType ? getTypeAncestors(objType, vocab.typeHierarchy) : null;

  for (const [key, value] of Object.entries(record)) {
    const currentPath = path ? `${path}.${key}` : key;

    // Skip JSON-LD keywords
    if (key.startsWith('@')) continue;

    // Check if property exists
    if (!vocab.properties.has(key)) {
      errors.push({
        path: currentPath,
        message: `Unknown property "${key}"`,
        type: 'unknown_property',
      });
      continue;
    }

    // Check if property is valid for this type
    if (objType && typeAncestors) {
      const domains = vocab.propertyDomains[key];
      if (domains && domains.length > 0) {
        const isValidForType = domains.some((domain) => typeAncestors.has(domain));
        if (!isValidForType) {
          errors.push({
            path: currentPath,
            message: `Property "${key}" is not valid for type "${objType}" (valid for: ${domains.join(', ')})`,
            type: 'invalid_property_for_type',
          });
        }
      }
    }

    // Recurse into nested objects/arrays
    if (Array.isArray(value)) {
      value.forEach((item, i) => {
        errors.push(...validateObject(item, vocab, `${currentPath}[${i}]`));
      });
    } else if (typeof value === 'object' && value !== null) {
      errors.push(...validateObject(value, vocab, currentPath));
    }
  }

  return errors;
}

/**
 * Validate a JSON-LD schema object against the latest schema.org vocabulary.
 * Returns empty array if vocab file is not available.
 */
export function validateSchemaOrg(data: unknown): ValidationError[] {
  const vocab = loadVocabulary();
  if (vocab == null) return [];
  return validateObject(data, vocab);
}

/**
 * Escapes '<' characters in JSON-LD strings to prevent XSS attacks.
 * User-controlled fields containing '</script>' could prematurely close
 * the script tag and inject malicious content during SSR.
 */
export function escapeJsonLd(json: string): string {
  return json.replace(/</g, '\\u003c');
}
